import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { queryKeys } from '@api/queryKeys';
import type { BrandingConfig } from '@shared/types/branding';
import type { BrandingThemeTokens } from './theme/buildTheme';

const CACHE_KEY = 'bv-branding';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedBranding {
  data: BrandingConfig;
  cachedAt: number;
}

const defaultBranding: BrandingConfig = {
  appName: 'BV Terminal',
  logoUrl: '/logo.svg',
  faviconUrl: '/favicon.svg',
};

function loadCachedBranding(): BrandingConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedBranding = JSON.parse(raw);
    if (Date.now() - cached.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
}

function saveCachedBranding(data: BrandingConfig): void {
  try {
    const cached: CachedBranding = { data, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

function updateFavicon(url: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function extractTokens(branding: BrandingConfig): BrandingThemeTokens | undefined {
  const t = branding.themeTokens;
  if (!t) return undefined;
  const tokens: BrandingThemeTokens = {};
  if (t.primary) tokens.primaryColor = t.primary;
  if (t.secondary) tokens.secondaryColor = t.secondary;
  if (t.success) tokens.successColor = t.success;
  if (t.warning) tokens.warningColor = t.warning;
  if (t.error) tokens.errorColor = t.error;
  return Object.keys(tokens).length > 0 ? tokens : undefined;
}

interface Props {
  children: (tokens?: BrandingThemeTokens) => React.ReactNode;
}

/**
 * Fetches branding config from backend, caches in localStorage.
 * Falls back to cached or default branding on failure.
 * Always renders immediately with the best available branding.
 */
export function BrandingLoader({ children }: Props) {
  const cached = loadCachedBranding();

  const { data: branding } = useQuery({
    queryKey: queryKeys.branding,
    queryFn: () => apiClient.get<BrandingConfig>('/settings/branding'),
    staleTime: CACHE_TTL_MS,
    retry: 1,
  });

  const resolved = branding ?? cached ?? defaultBranding;
  const prevTitleRef = useRef<string | undefined>();
  const prevFaviconRef = useRef<string | undefined>();

  // Apply branding side-effects (external system sync only, no setState)
  useEffect(() => {
    if (resolved.appName && resolved.appName !== prevTitleRef.current) {
      document.title = resolved.appName;
      prevTitleRef.current = resolved.appName;
    }
    if (resolved.faviconUrl && resolved.faviconUrl !== prevFaviconRef.current) {
      updateFavicon(resolved.faviconUrl);
      prevFaviconRef.current = resolved.faviconUrl;
    }
    if (branding) {
      saveCachedBranding(branding);
    }
  }, [resolved.appName, resolved.faviconUrl, branding]);

  return <>{children(extractTokens(resolved))}</>;
}
